/* the fields the indexer pulls out of a query before what's left becomes the content
   pattern (indexer/src/index.rs::SYSTEM_FIELDS). kept in sync by hand: getting it
   wrong only ever means a highlight is missing, never a wrong search */
const SYSTEM_FIELDS = ['repo', 'path', 'rel_path', 'file_name', 'ext', 'lang', 'size', 'mtime']

function escapeRegex(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * the same pattern the server grepped with, rebuilt here so the file view can show
 * you *where* it matched. mirrors indexer/src/query.rs: `/.../` is a regex, anything
 * else is a literal, and both are case-insensitive until you type an uppercase letter.
 *
 * js and rust don't agree on every regex extension, so an expression that only rust
 * accepts simply doesn't highlight - the results themselves are unaffected
 */
export function patternOf(query: string): RegExp | null {
  const rest = query
    .split(/\s+/)
    .filter((token) => {
      const [field, ...value] = token.replace(/^-/, '').split(':')
      return !(SYSTEM_FIELDS.includes(field) && value.join(':').length > 0)
    })
    .join(' ')
    .trim()

  if (!rest) return null

  const asRegex = /^\/(.+)\/$/.exec(rest)
  const body = asRegex ? asRegex[1] : escapeRegex(rest)
  const flags = /[A-Z]/.test(asRegex ? asRegex[1] : rest) ? 'g' : 'gi'

  try {
    return new RegExp(body, flags)
  } catch {
    return null
  }
}

export interface TRange {
  start: number
  end: number
}

/** every match of `pattern` in one line, as character ranges */
export function matchRanges(line: string, pattern: RegExp): TRange[] {
  pattern.lastIndex = 0
  const out: TRange[] = []

  for (;;) {
    const match = pattern.exec(line)
    if (match === null) break

    // a zero-width match (`^`, `a*`) would spin here forever
    if (match[0].length === 0) {
      pattern.lastIndex += 1
      continue
    }
    out.push({ start: match.index, end: match.index + match[0].length })
    if (out.length > 200) break
  }

  return out
}
